import { cloneDeep } from 'lodash';
import { useState, useEffect } from 'react';
import workbookStorage from '@/services/storage/workbooks';
const PREFIX = 'common: hooks: useLoadWorkbookMetaFile:';
const EMPTY_META = {
  sheetNames: [],
  formats: []
}; // Download a workbook's optional meta data

export default function useLoadWorkbookMetaFile(storage, workbookStatus, workbook) {
  const {
    0: error,
    1: setError
  } = useState(null);
  const {
    0: status,
    1: setStatus
  } = useState('loading');
  const {
    0: meta,
    1: setMeta
  } = useState(cloneDeep(EMPTY_META)); // Request Download URL

  const {
    status: downloadStatus,
    error: downloadError,
    data: downloadUrl
  } = useDownloadWorkbookMetaUrl(storage, workbookStatus, workbook);
  useEffect(() => {
    // Return blank meta when workbook
    // has no metadata
    if (downloadStatus === 'success' && !downloadUrl) {
      setStatus('success');
      setMeta(cloneDeep(EMPTY_META));
      setError(null);
    }

    if (downloadStatus === 'failure') {
      const wrappedError = Error(`${PREFIX} useLoadWorkbookMeta: download URL failed: ${downloadError}`);
      setError(wrappedError);
      setStatus('failure');
      setMeta(cloneDeep(EMPTY_META)); // fail gracefully without formatting etc
    }

    if (!downloadUrl) return;

    (async function () {
      try {
        // Load workbook/spreadsheet from storage
        const data = await workbookStorage.downloadWorkbookMeta(`${downloadUrl}`);
        setMeta(data);
        setStatus('success');
        setError(null);
      } catch (err) {
        const wrappedError = Error(`${PREFIX} useLoadWorkbookMeta: failed to load workbook meta from "${downloadUrl}": ${err}`);
        setError(wrappedError);
        setStatus('failure');
        setMeta(cloneDeep(EMPTY_META)); // fail gracefully without formatting etc
      }
    })();
  }, [downloadStatus, downloadError, downloadUrl, workbook === null || workbook === void 0 ? void 0 : workbook.syncedAt]);
  return {
    error,
    status,
    data: meta,
    url: downloadUrl
  };
}
export function useDownloadWorkbookMetaUrl(storage, workbookStatus, workbook) {
  const {
    0: result,
    1: setResult
  } = useState({
    status: 'loading',
    error: null,
    data: ''
  });
  useEffect(() => {
    if (workbookStatus === 'loading') {
      setResult({
        status: 'loading',
        error: null,
        data: ''
      });
      return;
    }

    const storagePath = (workbook === null || workbook === void 0 ? void 0 : workbook.metaFileRef) || ''; // Workbook has no optional meta data
    // mark as completed and continue

    if (!storagePath) {
      setResult({
        status: 'success',
        error: null,
        data: ''
      });
      return;
    } // Lookup meta file download URL


    workbookStorage.getDownloadUrl(storage, storagePath).then(url => {
      setResult({
        data: url,
        status: 'success',
        error: null
      });
    }).catch(err => {
      const wrappedErr = Error(`${PREFIX} useDownloadWorkbookMetaUrl: failed to download workbook meta URL: "${(workbook === null || workbook === void 0 ? void 0 : workbook.metaFileRef) || 'unknown'}": ${err}`);
      setResult({
        error: wrappedErr,
        status: 'failure',
        data: ''
      });
    });
  }, [workbookStatus, workbook, storage]);
  return result;
}